import { Sheet } from '@climate-x-org/ui-kit'
import type { ReactNode } from 'react'
import { createContext, useContext, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

type DrawerContent = ReactNode | null

type OpenDrawerOptions = {
  content: DrawerContent
  onClose?: () => void
}

type DrawerContextType = {
  isOpen: boolean
  content: DrawerContent
  openDrawer: (options: OpenDrawerOptions) => void
  closeDrawer: () => void
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined)

type DrawerProviderProps = {
  children: ReactNode
}

export function DrawerProvider({ children }: DrawerProviderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [content, setContent] = useState<DrawerContent>(null)
  const onCloseRef = useRef<(() => void) | undefined>(undefined)

  const openDrawer = (options: OpenDrawerOptions) => {
    setContent(options.content)
    onCloseRef.current = options.onClose
    setIsOpen(true)
  }

  const closeDrawer = () => {
    setIsOpen(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onCloseRef.current?.()
      setIsOpen(false)
      // Delay clearing content until animation completes
      setTimeout(() => {
        setContent(null)
        onCloseRef.current = undefined
      }, 300)
    }
  }

  return (
    <DrawerContext.Provider
      value={{ isOpen, content, openDrawer, closeDrawer }}
    >
      {children}
      <DrawerView
        isOpen={isOpen}
        content={content}
        onOpenChange={handleOpenChange}
      />
    </DrawerContext.Provider>
  )
}

export function useDrawer(): DrawerContextType {
  const context = useContext(DrawerContext)
  if (context === undefined) {
    throw new Error('useDrawer must be used within a DrawerProvider')
  }
  return context
}

type DrawerContainerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  children: ReactNode
  width?: string
}

function DrawerContainer({
  open,
  onOpenChange,
  title,
  description,
  children,
  width = 'w-[500px]',
}: DrawerContainerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <Sheet.Content
        side='right'
        className={cn(
          width,
          'fixed inset-y-0 right-0 z-50 h-full border-l bg-white p-0 shadow-lg outline-none',
          'data-[state=open]:animate-drawer-in data-[state=closed]:animate-drawer-out',
          'overflow-visible'
        )}
      >
        <Sheet.Header className='sr-only'>
          <Sheet.Title>{title}</Sheet.Title>
          {description && <Sheet.Description>{description}</Sheet.Description>}
        </Sheet.Header>

        <div className='mt-8 h-[calc(100vh-5rem)] min-h-0 min-w-0 overflow-y-auto'>
          <div className='flex h-full min-h-full min-w-0 flex-col px-6 pb-6'>
            {children}
          </div>
        </div>
      </Sheet.Content>
    </Sheet>
  )
}

function DrawerView({
  isOpen,
  content,
  onOpenChange,
}: {
  isOpen: boolean
  content: ReactNode
  onOpenChange: (open: boolean) => void
}) {
  if (!content && !isOpen) return null

  return (
    <DrawerContainer
      open={isOpen}
      onOpenChange={onOpenChange}
      title='Drawer'
      description='Content drawer'
    >
      {content}
    </DrawerContainer>
  )
}

export const Drawer = Object.assign(
  {},
  {
    Provider: DrawerProvider,
    Container: DrawerContainer,
  }
)
